import type {
  ModStatus,
  ProductKind,
  PublicationTree,
  PublicationUpdateIn,
} from '@/api/generated/schemas';
import type { ApiErrorDetailItem } from '@/utils/apiError';

/** What one publication's moderation request can change: itself or a format. */
export type ModStatusTarget = 'publication' | ProductKind;

/** Status picked per target; a target left out keeps its status. */
export type ModStatusDraft = Partial<Record<ModStatusTarget, ModStatus>>;

const isProductKind = (value: unknown): value is ProductKind =>
  value === 'ebook' || value === 'audio';

/** Statuses as they are now: the publication and each of its formats. */
export const currentStatuses = (tree: PublicationTree): ModStatusDraft => ({
  publication: tree.publication.mod_status,
  ...Object.fromEntries(
    (tree.products ?? []).map((p) => [p.kind, p.mod_status] as const)
  ),
});

/** Targets whose picked status differs from the current one. */
export const changedTargets = (
  tree: PublicationTree,
  draft: ModStatusDraft
): ModStatusTarget[] => {
  const current = currentStatuses(tree);
  return (Object.keys(draft) as ModStatusTarget[]).filter(
    (target) =>
      target in current &&
      draft[target] != null &&
      draft[target] !== current[target]
  );
};

/**
 * `PATCH /user_catalog/publications/{id}` body carrying only what changed —
 * the publication's own `mod_status` and each format as `{id, mod_status}`.
 * `null` when nothing changed. The backend applies it whole or refuses it
 * whole, so the targets may be sent together.
 */
export const buildModStatusPatch = (
  tree: PublicationTree,
  draft: ModStatusDraft
): PublicationUpdateIn | null => {
  const changed = changedTargets(tree, draft);
  if (changed.length === 0) return null;
  const patch: PublicationUpdateIn = {};
  for (const target of changed) {
    const status = draft[target];
    if (status == null) continue;
    if (target === 'publication') {
      patch.mod_status = status;
      continue;
    }
    const product = tree.products?.find((p) => p.kind === target);
    if (product) patch[target] = { id: product.id, mod_status: status };
  }
  return patch;
};

/**
 * Where a refusal of the PATCH belongs: `body.mod_status` is the
 * publication's, `body.<kind>.mod_status` a format's. Anything else (a
 * plain validation error) has no row.
 */
const targetOf = (loc: ApiErrorDetailItem['loc']): ModStatusTarget | null => {
  const [, first, second] = loc ?? [];
  if (first === 'mod_status') return 'publication';
  if (isProductKind(first) && second === 'mod_status') return first;
  return null;
};

export interface ModStatusRefusals {
  byTarget: Partial<Record<ModStatusTarget, string[]>>;
  /** Messages no row can carry. */
  general: string[];
}

/**
 * Refusal messages per row. An `author_disabled` refusal names the authors
 * at fault (`ctx.author_ids`) — the bare rule does not say which.
 */
export const refusalsByTarget = (
  items: ApiErrorDetailItem[],
  authorName: (id: number) => string | undefined
): ModStatusRefusals => {
  const byTarget: ModStatusRefusals['byTarget'] = {};
  const general: string[] = [];
  for (const item of items) {
    if (!item.msg) continue;
    const authorIds = Array.isArray(item.ctx?.author_ids)
      ? item.ctx.author_ids.filter((id): id is number => typeof id === 'number')
      : [];
    const message =
      item.type === 'author_disabled' && authorIds.length > 0
        ? `${item.msg}: ${authorIds.map((id) => authorName(id) ?? `#${id}`).join(', ')}`
        : item.msg;
    const target = targetOf(item.loc);
    if (target) (byTarget[target] ??= []).push(message);
    else general.push(message);
  }
  return { byTarget, general };
};
